/**
 * Hover methodology for every Quarter-at-a-Glance figure: which
 * Schedule-of-Work / WiP-ledger columns feed it and exactly how the
 * number is worked out. Rendered as an info icon + tooltip.
 */
import { createContext, useContext } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

// Info icons only show while building the report (editor), never on
// the published/public view.
export const InfoTipContext = createContext(false);

export const METHODOLOGY = {
  turnover: {
    where: "WiP ledger (invoice instalments synced from the SharePoint WiP list).",
    how: "Sum of invoices in the quarter. Cancelled projects excluded; team figures use each project's Team in charge.",
  },
  proposals_submitted: {
    where: "Schedule of Work → PROPOSAL DATE, with revisions linked by the OCID column, plus MediLAB quotes from the MediLAB order form.",
    how: "Opportunities quoted this quarter. Revised executions of the same proposal count as ONE opportunity within the quarter; a re-quote of a proposal first issued in an earlier quarter counts as this quarter's opportunity. MediLAB language-service quotes (translation, transcription, sim-trans) count as opportunities on their own team lane.",
  },
  unique_clients: {
    where: "Schedule of Work → Client Company on this quarter's opportunities, plus MediLAB order-form clients.",
    how: "Distinct company names among the quarter's deduped opportunities. 'Brand-new' means the company family had never been quoted before this quarter started (full history back to 2022, tolerant of office suffixes like (US)/(Spain) and legal endings); busiest clients rank by number of opportunities quoted. Team figures count each client once per team, so a client quoted by two teams appears in both.",
  },
  avg_proposal_value: {
    where: "Schedule of Work → TO (£), always taken from the ORIGINAL version of each proposal.",
    how: "Mean originator TO (£) per opportunity quoted this quarter; blank or zero values excluded. Revised executions don't add extra data points. Total value quoted = the sum across all opportunities; the median shows the typical quote (immune to a few monster quotes); the size-mix bar counts quotes per value band.",
  },
  submitted_commissioned: {
    where: "Schedule of Work → PROPOSAL DATE + COM DATE across each proposal's chain of versions.",
    how: "Of the quarter's opportunities, how many have (ever) been won. An opportunity can only be won once - if a later execution is the version commissioned, that still counts as one proposal and one win.",
  },
  projects_commissioned: {
    where: "Schedule of Work → COM DATE + TO (£) + STAT, plus commissioned MediLAB orders.",
    how: "Everything commissioned this quarter, whenever it was quoted; multiple versions of the same proposal count once. Value won = the sum of those projects' TO (£); the biggest wins are named. Team splits use each project's Team in charge; MediLAB commissions count on their own lane. If any of the quarter's wins were later CANCELLED, the card says so with the £ walked away (they stay counted as wins - the sale succeeded).",
  },
  avg_project_value: {
    where: "Schedule of Work → TO (£) on this quarter's commissions.",
    how: "Mean TO (£) of projects commissioned this quarter; blanks excluded.",
  },
  projects_delivered: {
    where: "Schedule of Work → FINISHED DATE (invoice date as fallback), Team in charge.",
    how: "Rows finished inside the quarter; rows never marked finished count on their invoiced date instead. Chips split by the Team in charge column.",
  },
  projects_started: {
    where: "Schedule of Work → IN FIELD DATE, Team in charge.",
    how: "Rows whose IN FIELD DATE falls inside the quarter.",
  },
  avg_days_in_field: {
    where: "Schedule of Work → IN FIELD DATE and FINISHED DATE.",
    how: "Mean days between going in field and finishing, for projects delivered this quarter (gaps over 500 days treated as data errors and dropped).",
  },
  in_field_now: {
    where: "Schedule of Work → STAT column.",
    how: "Rows whose status reads IN FIELD today. A live snapshot of the current pipeline, not quarter-bound.",
  },
  awaiting_contracts: {
    where: "Schedule of Work → STAT column.",
    how: "Rows whose status reads COMMISSIONED today - won, waiting on contracts. Live snapshot.",
  },
  about_to_start: {
    where: "Schedule of Work → STAT column.",
    how: "Rows whose status reads SOW SIGNED or BRIEFED today - contracted and queued to go in field. Live snapshot.",
  },
  win_rate: {
    where: "Schedule of Work → PROPOSAL DATE + COM DATE across each proposal's chain of versions (research teams), plus the MediLAB order form (reported separately).",
    how: "Share of the quarter's research-team opportunities (revisions deduped) that have (ever) been won - one win per opportunity. Because clients can take months to decide, a recent quarter reads low at first and keeps rising as open quotes convert. To keep the comparison fair, the badges compare against what each quarter had converted at the SAME point in its life, not its final matured rate (the final rate is in the badge hover). The card also shows how much past quarters climbed after this stage, and the win rate among quotes already decided (won ÷ won+lost). MediLAB is shown separately: its orders convert the day they are quoted and the form has no lost status, so a MediLAB quote not taken up within 60 days counts as lost.",
  },
  prop_to_com_days: {
    where: "Schedule of Work → PROPOSAL DATE of the ORIGINAL quote and COM DATE of the winning version, Team in charge.",
    how: "Median days from first quote to commission, for opportunities won this quarter. Median (not mean) so one slow deal can't skew it. Team lanes show each team's own median with its sample size - hover a lane for last year's figure. The since-2023 badge compares against the long-run median across every win since Jan 2023. MediLAB orders are excluded here: they are commissioned the day they are quoted, which would flatter the median without saying anything about deal speed.",
  },
  sow_to_field_days: {
    where: "Schedule of Work → SOWDATE and IN FIELD DATE, Team in charge.",
    how: "Median days from contract signature to going in field, for projects started this quarter. Team lanes show each team's own median with its sample size - small samples move around, so read them gently. The since-2023 badge compares against the long-run median across every project since Jan 2023.",
  },
  field_to_finish_days: {
    where: "Schedule of Work → IN FIELD DATE and FINISHED DATE, Team in charge.",
    how: "Median days from fieldwork starting to fieldwork finishing, for projects finished this quarter. Median (not mean) so one marathon project cannot skew it. Team lanes show each team's own median with its sample size - small samples move around, so read them gently. The since-2023 badge compares against the long-run median across every project since Jan 2023.",
  },
  medilab_turnaround: {
    where: "MediLAB order form → Commissioning date and Delivery date on each commissioned order.",
    how: "Median days from the order being commissioned to its delivery date, for MediLAB orders commissioned this quarter. MediLAB quotes convert the day they are sent, so quote-to-win speed is not meaningful for them; delivery turnaround is their speed story. Median, so one slow job cannot skew it.",
  },
  journey: {
    where: "Schedule of Work → COM DATE, SOW DATE, IN FIELD DATE, FINISHED DATE and the final invoice date (project 100% complete).",
    how: "A guide to the typical rhythm of a project, from commission through to the final invoice. Each leg shows the typical (median) days between two milestones across our projects. It is meant as a rough guide, not an exact measurement.",
  },
  recommend: {
    where: "Schedule of Work → CSS column (end-of-project survey answer).",
    how: "Survey answers for projects delivered this quarter, one square per client: 'I definitely will' / 'I might do' / 'I definitely will not' recommend Medicys.",
  },
  new_clients: {
    where: "Schedule of Work → Client Company + COM DATE.",
    how: "Companies whose first-ever COM DATE falls inside this quarter - genuinely new relationships, not repeat business (name matching ignores office suffixes like (US)/(Spain) and legal endings). £ = their commissions this quarter (TO £). 'New doors opened' below lists companies quoted for the very first time - not yet won.",
  },
  topics: {
    where: "Schedule of Work → Topic column.",
    how: "Topics with a PROPOSAL DATE this quarter (the top 30 shown). Bigger text = more quotes; hover any topic for its exact count.",
  },
  repeat_business: {
    where: "Schedule of Work → Client Company + COM DATE, from January 2023 onward.",
    how: "Companies with 2+ commissioned projects ÷ all companies commissioned since Jan 2023. The wall ranks repeat clients by DEPTH (number of commissioned projects), with years since their first counted commission alongside. Data before Jan 2023 is unreliable and excluded across the app.",
  },
  lost_reasons: {
    where: "Schedule of Work → STAT = LOST/CANCELLED + the LOST CHOICE column.",
    how: "This quarter's deduped opportunities where every version is now LOST or CANCELLED. The reasons are those the CLIENT gave us for not going ahead, as recorded by the BD team at the time, with the TO (£) alongside. Where a client did not tell us why after being chased 3 times over 3 months, the quote is counted as No Response; 'Unknown' means no reason was logged.",
  },
  work_mix: {
    where: "Schedule of Work → Qual/Quant and Study Type columns, FINISHED DATE.",
    how: "Projects delivered this quarter - a FINISHED DATE inside the quarter - one bar per category. The solid bar is this quarter; the dark tick marks where the same quarter last year sat, and the arrow chip shows the shift in projects. A shift is a change in mix, not a score.",
  },
  seasonality: {
    where: "Schedule of Work → IN FIELD DATE and FINISHED DATE, from January 2023.",
    how: "Each project counts once for every calendar month it was in field (months before Jan 2023 excluded as unreliable), totalled across all years. Darker = historically busier.",
  },
  milestones: {
    where: "Schedule of Work → IN FIELD DATE and FINISHED DATE (fastest delivery), plus every complete quarter since Jan 2023 for the records.",
    how: "Fastest = shortest field-to-finish among this quarter's deliveries. A gold 'All-time record' card appears only when this quarter beats every previous quarter since Jan 2023 on that measure (turnover, opportunities quoted, value quoted, value won, new clients or deliveries) - no card means no record broken.",
  },
  records: {
    where: "Schedule of Work + WiP ledger, every complete quarter since Jan 2023.",
    how: "Each measure is computed per quarter the same way as its headline figure; a record card appears only when this quarter strictly beats every previous quarter.",
  },
  chart_outcome: {
    where: "Schedule of Work → this quarter's opportunities (revisions deduped via OCID) + STAT and COM DATE.",
    how: "Won = any version commissioned. Lost = every version marked LOST or CANCELLED. Still being decided = everything else - these can still convert into wins.",
  },
  chart_team: {
    where: "Schedule of Work → Team in charge on the ORIGINAL version of each opportunity.",
    how: "This quarter's deduped opportunities, split by which team quoted them.",
  },
  chart_trend: {
    where: "Schedule of Work → PROPOSAL DATE, revisions deduped via OCID.",
    how: "Opportunities quoted per quarter over the last 8 quarters, counted the same deduped way as the headline figure.",
  },
  lifecycle: {
    where: "Schedule of Work → COM DATE, SOWDATE, BRIEF DATE, IN FIELD DATE, FINISHED DATE, DOI (invoiced), Team in charge, STAT.",
    how: "Each step counts projects whose milestone date falls inside the quarter (commissions dedupe revisions, matching the hero). A project commissioned last quarter and finished this quarter appears under 'Finished' only - steps are milestones hit this quarter, not one cohort flowing through. Day gaps = average days between consecutive milestones, across the projects delivered this quarter. 'Right now' is a live snapshot by STAT as of today: IN FIELD; COMMISSIONED (awaiting contracts); SOW SIGNED or BRIEFED (about to start).",
  },
};

export function InfoTip({ id, light = false }) {
  const enabled = useContext(InfoTipContext);
  const m = METHODOLOGY[id];
  if (!enabled || !m) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`shrink-0 transition-colors ${
            light ? "text-stone-400 hover:text-white" : "text-stone-300 hover:text-[#1B4F8A]"
          }`}
          data-testid={`qkpi-info-${id}`}
          aria-label="How this figure is calculated"
        >
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs bg-slate-900 text-white border border-slate-700 rounded-xl px-3.5 py-3 shadow-xl"
      >
        <p className="text-[9px] font-mono uppercase tracking-widest text-teal-300">Where the data lives</p>
        <p className="text-[11px] leading-snug mt-0.5 text-stone-100">{m.where}</p>
        <p className="text-[9px] font-mono uppercase tracking-widest text-teal-300 mt-2.5">How it&apos;s worked out</p>
        <p className="text-[11px] leading-snug mt-0.5 text-stone-100">{m.how}</p>
      </TooltipContent>
    </Tooltip>
  );
}
